const moneyFormat = '${{amount_no_decimals}}';

function formatMoney(cents, format) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  let value = '';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || moneyFormat;

  function formatWithDelimiters(
      number,
      precision = 2,
      thousands = ',',
      decimal = '.'
  ) {
    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        `$1${thousands}`
    );
    const centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }

  return formatString.replace(placeholderRegex, value);
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', {bubbles: true});
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {

    if (this.input.max && (parseInt(this.input.max) > 0) && (this.input.value + 1 > this.input.max)) {

      this.querySelector(".quantity__button[name='plus']").classList.add('disabled');
    } else {

      this.querySelector(".quantity__button[name='plus']").classList.remove('disabled');
    }
    // if(this.input.value - 1 <= (this.input.min || 0)) {
    //   this.querySelector(".quantity__button[name='minus']").classList.add('disabled');
    // } else {
    //   this.querySelector(".quantity__button[name='minus']").classList.remove('disabled');
    // }
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (this.input.max && (parseInt(this.input.max) > 0) && (this.input.value + 1 > this.input.max)) {
      this.querySelector(".quantity__button[name='plus']").classList.add('disabled');
    } else {
      this.querySelector(".quantity__button[name='plus']").classList.remove('disabled');
    }
    // if(this.input.value - 1 <= (this.input.min || 0)) {
    //   this.querySelector(".quantity__button[name='minus']").classList.add('disabled');
    // } else {
    //   this.querySelector(".quantity__button[name='minus']").classList.remove('disabled');
    // }
    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      if (max > 0) {
        buttonPlus.classList.toggle('disabled', value >= max);
      }
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {'Content-Type': 'application/json', Accept: `application/${type}`},
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    // document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    // document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.modalContent = document.querySelector('.search-modal');
    this.modalOverlay = document.querySelector('.modal-overlay');
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.header.classList.add('header-bg-scroll-down-action');
    if (!this.header.classList.contains('header-active')) {
      this.header.classList.add('header-active');
    }
    this.header.classList.remove('header-bg-scroll-up-action');
    this.header.classList.remove('header-bg-scroll-up');
    this.header.classList.remove('header-bg-scroll-down');

    if (this.modalOverlay.classList.contains('modal-overlay-open')) {
      this.modalContent.animate([
        {opacity: 1, transform: 'translateY(5.4rem)'},    // Fully visible
        {opacity: 0, transform: 'translateY(0rem)'}  // Fully hidden
      ], {
        duration: 200,
        easing: 'ease-out',
        fill: 'forwards'
      })
    }
    this.modalOverlay.classList.remove('modal-overlay-open');
    document.querySelector('.header__search .summary').removeAttribute('open');
    // document.body.classList.add('overflow-hidden');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    // document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    this.header.classList.remove('header-active');
    if (!this.header.classList.contains('scrolled-past-header')) {
      this.header.classList.add('header-bg-scroll-up');
      this.header.classList.remove('header-bg-scroll-down');
    }
    this.header.classList.remove('header-bg-scroll-down-action');
    this.header.classList.remove('header-bg-scroll-up-action');

    // document.body.classList.remove('overflow-hidden');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    // document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    // document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}

customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}

customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => {
      requestAnimationFrame(() => this.initPages());
    });
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }


    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          {once: true}
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
      (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({target}) {
    const {value, tagName} = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({dataset}) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
    this.slidesDesktop = this.dataset.slidesShowDesktop;
    this.slidesMobile = this.dataset.slidesShowMobile;
    this.swiperId = this.dataset.swiperId;
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
    this.toggleSliderButtons()
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      {rootMargin: '0px 0px 400px 0px'}
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
        if (this.dataset.swiperEnable === "true") {
          this.initializeSwiper();
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  initializeSwiper() {
    const swiper = new Swiper(`.${this.swiperId}`, {
      loop: true,
      speed: 600,
      slidesPerView: 6,
      centerInsufficientSlides: true,
      spaceBetween: 0,
      navigation: {
        nextEl: '.slider-button--next',
        prevEl: '.slider-button--prev',
      },
      breakpoints: {
        350: {
          slidesPerView: 1.8,
          centerInsufficientSlides: false,
          centeredSlides: true,
        },
        981: {
          slidesPerView: 5,
          centerInsufficientSlides: true,
        },
        2000: {
          slidesPerView: 8,
          centerInsufficientSlides: true,
        },
      },
      on: {
        init: () => this.toggleSliderButtons(),
        resize: () => this.toggleSliderButtons(),
      }
    });
  }

  toggleSliderButtons() {
    const swiperSlides = this.querySelectorAll('.swiper-slide');

    const sliderButtons = this.querySelector('.slider-buttons');

    const screenWidth = window.innerWidth;

    if (!swiperSlides || !sliderButtons) {
      return
    }

    if (screenWidth >= 370 && screenWidth < 980) {
      if (swiperSlides.length <= 3) {
        sliderButtons.style.display = 'none';
      } else {
        sliderButtons.style.display = 'block';
      }
    }
    if (screenWidth >= 370 && screenWidth < 980) {
      if (swiperSlides.length <= 5) {
        sliderButtons.style.display = 'none';
      } else {
        sliderButtons.style.display = 'block';
      }
    }
    // Условие для экранов от 981 до 2000 пикселей
    if (screenWidth >= 981 && screenWidth <= 2000) {
      if (swiperSlides.length <= 5) {
        sliderButtons.style.display = 'none';
      } else {
        sliderButtons.style.display = 'block';
      }
    }
    // Условие для экранов больше 2001 пикселя
    else if (screenWidth > 2000) {
      if (swiperSlides.length <= 8) {
        sliderButtons.style.display = 'none';
      } else {
        sliderButtons.style.display = 'block';
      }
    }
  }
}

customElements.define('product-recommendations', ProductRecommendations);

// function toggleSliderButtons() {
//   const swiperSlides = document.querySelectorAll('.related-products .swiper-slide');
//
//   const sliderButtons = document.querySelector('.related-products .slider-buttons');
//
//   const screenWidth = window.innerWidth;
//
//   if (!swiperSlides || !sliderButtons) {
//     return
//   }
//
//   if (screenWidth >= 370 && screenWidth < 980) {
//     if (swiperSlides.length <= 3) {
//       sliderButtons.style.display = 'none';
//     } else {
//       sliderButtons.style.display = 'block';
//     }
//   }
//   if (screenWidth >= 370 && screenWidth < 980) {
//     if (swiperSlides.length <= 5) {
//       sliderButtons.style.display = 'none';
//     } else {
//       sliderButtons.style.display = 'block';
//     }
//   }
//   // Условие для экранов от 981 до 2000 пикселей
//   if (screenWidth >= 981 && screenWidth <= 2000) {
//     if (swiperSlides.length <= 5) {
//       sliderButtons.style.display = 'none';
//     } else {
//       sliderButtons.style.display = 'block';
//     }
//   }
//   // Условие для экранов больше 2001 пикселя
//   else if (screenWidth > 2000) {
//     if (swiperSlides.length <= 8) {
//       sliderButtons.style.display = 'none';
//     } else {
//       sliderButtons.style.display = 'block';
//     }
//   }
// }

// toggleSliderButtons()
// window.addEventListener('resize', toggleSliderButtons);

// Вызываем функцию при загрузке страницы
// window.addEventListener('load', toggleSliderButtons);

// Вызываем функцию при изменении размера окна


class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({id, quantity});
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}


if (!customElements.get('mega-menu')) {
  class MegaMenu extends HTMLElement {
    constructor() {
      super();
      this.parentLink = this.dataset.parentLink;
      this.link = document.querySelector(`#HeaderMenu-${this.parentLink}`);
      this.megaMenuSection = document.querySelector(`#shopify-section-${this.dataset.sectionId}`);
      if (this.link) {
        this.link.addEventListener('mouseover', () => {
          this.showMenu();
        });

        this.link.addEventListener('mouseout', (event) => {
          if (!this.contains(event.relatedTarget) && !this.isMouseOverMenu(event)) {
            this.hideMenu();
          }
        });

        // Keep the menu visible when hovering over it
        this.addEventListener('mouseover', () => {
          this.showMenu();
        });

        // Optional: Hide the menu when the mouse leaves the menu area
        this.addEventListener('mouseleave', (event) => {
          if (!this.contains(event.relatedTarget) && !this.isMouseOverMenu(event)) {
            this.hideMenu();
          }
        });
      }
    }

    showMenu() {
      const linkRect = this.link.getBoundingClientRect();
      this.style.top = `${linkRect.bottom}px`;
      this.megaMenuSection.style.top = `${linkRect.bottom}px`;
      this.style.display = 'block';
    }

    hideMenu() {
      this.style.display = 'none';
    }

    isMouseOverMenu(event) {
      // Check if the mouse is over the mega menu itself
      const menuRect = this.getBoundingClientRect();
      return (
        event.clientX >= menuRect.left &&
        event.clientX <= menuRect.right &&
        event.clientY >= menuRect.top &&
        event.clientY <= menuRect.bottom
      );
    }
  }

  customElements.define('mega-menu', MegaMenu);

}

if (!customElements.get('popup-trigger')) {
  customElements.define(
    'popup-trigger',
    class PopupTrigger extends HTMLElement {
      constructor() {
        super();
        this.triggerId = this.dataset.popupTriggerId;

        this.addEventListener('click', this.openPopup.bind(this));
      }

      openPopup() {
        const popup = document.querySelector(`[data-popup-id="${this.triggerId}"]`);

        if (popup) {
          popup.classList.add('popup-open');
          // document.body.classList.add('no-scroll');
        } else {
          console.error(`Popup with ID ${this.triggerId} not found.`);
        }
      }
    }
  )
}


class PopupWrapper extends HTMLElement {
  constructor() {
    super();
    this.closeButtons = this.querySelectorAll('[data-popup-close]');
    if (this.closeButtons.length > 0) {
      this.closeButtons.forEach(btn => btn.addEventListener('click', this.closePopup.bind(this)))

    }
    this.addEventListener('click', this.handleDocumentClick.bind(this));
  }

  handleDocumentClick(event) {
    const popupContent = this.querySelector('.popup-content');
    if (this.classList.contains('popup-open') && !popupContent.contains(event.target)) {
      this.closePopup();
    }
  }

  closePopup() {
    this.classList.remove('popup-open');
    // document.body.classList.remove('no-scroll');
  }
}

customElements.define('popup-wrapper', PopupWrapper)

class LocalizationPopupWrapper extends PopupWrapper {
  constructor() {
    super();
    this.selectCountryBtn = this.querySelector('.popup-select-country');
    this.countryForm = this.querySelector('#PopupCountryForm');
    this.continueShoppingBtn = this.querySelector('.continue-shopping-btn');
    this.selectCountryBtn.addEventListener('click', this.onSelectCountryButton.bind(this));
    this.submitBtn = this.querySelector('button[type="submit"]');
    this.selectCountry = this.querySelector('.localization-select');
    this.selectCountry.addEventListener('change', this.onChangeCountry.bind(this));
  }

  connectedCallback() {
    this.countryForm.classList.add('hidden');
    this.submitBtn.setAttribute('disabled', true);
  }

  onSelectCountryButton() {
    this.continueShoppingBtn.classList.add('hidden');
    this.selectCountryBtn.classList.add('hidden');
    this.countryForm.classList.remove('hidden');
  }

  onChangeCountry() {
    if (this.submitBtn.hasAttribute('disabled')) {
      this.submitBtn.removeAttribute('disabled');
    }
  }
}

customElements.define('localization-popup-wrapper', LocalizationPopupWrapper)

if (!customElements.get('load-more-grid')) {
  customElements.define(
    'load-more-grid',
    class LoadMoreGrid extends HTMLElement {
      constructor() {
        super();
        this.button = this.querySelector('#load-more');
        this.buttonText = this.button.querySelector('.load-more-text');
        this.buttonLoader = this.button.querySelector('.loading__spinner');
        this.grid = this.querySelector('.product-grid');
        this.button?.addEventListener('click', this.loadMore.bind(this));
        this.productsCount = +this.button.dataset.pageItems;
        this.currentPage = +this.button.dataset.page;
        this.currentProductsCount = +this.grid.querySelectorAll('.grid__item').length;
        this.currentProductsCountEl = this.querySelector('.load-more-current-count');
        this.loadMoreBLock = this.querySelector('.load-more-block');

        if (this.currentProductsCountEl.innerText === '') {
          this.currentProductsCountEl.innerText = this.currentProductsCount;
        }
      }

      loadMore() {
        const url = new URL(window.location);
        url.searchParams.set('page', this.currentPage + 1);
        this.buttonText.classList.add('hidden');
        this.buttonLoader.classList.remove('hidden');
        fetch(url)
          .then(response => response.text())
          .then(data => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(data, 'text/html');
            const newProducts = doc.querySelectorAll('.product-grid .grid__item');
            if (newProducts.length) {
              newProducts.forEach(item => {
                this.grid.appendChild(item);
              });
            }
            this.currentProductsCountEl.innerHTML = +this.currentProductsCount + newProducts.length;
            this.currentProductsCount = +this.currentProductsCount + newProducts.length;
            this.button.setAttribute('data-page', this.currentPage + 1)
            this.currentPage = this.currentPage + 1;
            if (this.currentProductsCount == this.productsCount) {
              // Hide the button if no more products are available
              this.button.classList.add('hidden');
              this.loadMoreBLock.classList.add('hidden');
            }
            this.buttonText.classList.remove('hidden');
            this.buttonLoader.classList.add('hidden');
          })
          .catch(error => console.error('Error loading more products:', error));
      }
    }
  );
}

if (!customElements.get('email-validate')) {
  customElements.define(
    'email-validate',
    class EmailValidate extends HTMLElement {
      constructor() {
        super();
        this.errorMessage = this.querySelector('.form-message-blank');
        this.errorMessageValidate = this.querySelector('.form-message-validate');
        this.input = this.querySelector('.field input')
        this.submitBtn = this.querySelector('button[type="submit"]');

        //handlers
        this.input.addEventListener('input', this.validateEmail.bind(this));
        this.input.addEventListener('focus', this.validateEmail.bind(this));
        this.input.addEventListener('blur', this.handleBlur.bind(this));
      }

      validateEmail() {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        // if((this.input.value === '')) {
        //   this.errorMessage.classList.remove('form-message-none');
        //   this.errorMessageValidate.classList.add('form-message-none');
        //   this.submitBtn.setAttribute('disabled', true);
        //   this.submitBtn.classList.add('newsletter-form__button-disabled');
        // }
        // else 
        if (emailPattern.test(this.input.value) && this.input.value !== '') {
          this.errorMessageValidate.classList.add('form-message-none');
          this.errorMessage.classList.add('form-message-none');
          this.submitBtn.removeAttribute('disabled');
          this.submitBtn.classList.remove('newsletter-form__button-disabled');
        } else if (emailPattern.test(this.input.value) || this.input.value === '') {
          this.errorMessageValidate.classList.add('form-message-none');
          this.errorMessage.classList.add('form-message-none');
        } else {
          this.errorMessageValidate.classList.remove('form-message-none');
          // this.errorMessage.classList.add('form-message-none');
          this.submitBtn.setAttribute('disabled', true);
          this.submitBtn.classList.add('newsletter-form__button-disabled');
        }
      }

      handleBlur() {
        this.errorMessage.classList.add('form-message-none');
      }
    }
  )
}

if (!customElements.get('swiper-slider')) {
  customElements.define(
    'swiper-slider', class SwiperSlider extends HTMLElement {
      constructor() {
        super();
        this.swiper = this.querySelector('.swiper')
        this.swiperWrapper = this.querySelector('.swiper-wrapper-element')
        window.addEventListener("resize", this.debounce(this.onResize.bind(this), 200));
      }

      connectedCallback() {
        this.initializeSwiper();
      }

      initializeSwiper() {
        if (window.innerWidth < 768) {
          this.swiperWrapper.classList.add('swiper-wrapper');
          this.swiperWrapper.classList.remove('grid');
          new Swiper(`.${this.dataset.swiperId}`, {
            loop: true,
            speed: 600,
            slidesPerView: 1,
            spaceBetween: 10,
            navigation: {
              nextEl: '.slider-button--next',
              prevEl: '.slider-button--prev',
            },
          });
        } else {
          if (this.swiper) {
            this.swiperWrapper.classList.remove('swiper-wrapper');
            this.swiperWrapper.classList.add('grid');
            this.swiper.destroy(true, true);
            this.swiper = null;
          }
        }

      }

      debounce(func, delay) {
        let timeout;
        return function (...args) {
          clearTimeout(timeout);
          timeout = setTimeout(() => func.apply(this, args), delay);
        };
      }

      onResize() {
        this.initializeSwiper();
      }
    }
  )
}

if (!customElements.get('custom-accordion')) {
  customElements.define(
    'custom-accordion',
    class AccordionElement extends HTMLElement {
      constructor() {
        super();
        // Reference the existing <details> element inside the custom element
        this.el = this.querySelector('details');
        this.summary = this.el.querySelector('summary');
        this.content = this.el.querySelector('.content');

        // Initialize state variables
        this.animation = null;
        this.isClosing = false;
        this.isExpanding = false;

        // Event listener
        this.summary.addEventListener('click', (e) => this.onClick(e));
      }

      onClick(e) {
        e.preventDefault();
        this.el.style.overflow = 'hidden';

        // If animation is ongoing, return to avoid conflicting animations
        if (this.isClosing || this.isExpanding) return;

        // If the element is closed, we expand it, otherwise we shrink it
        if (!this.el.open) {
          this.toggleAccordion(true); // Expand
        } else {
          this.toggleAccordion(false); // Shrink
        }
      }

      toggleAccordion(expand) {
        // Set the appropriate state for expanding or shrinking
        if (expand) {
          this.isExpanding = true;
        } else {
          this.isClosing = true;
        }

        // Cache start height
        const startHeight = `${this.el.offsetHeight}px`;

        // If expanding, force the open state to calculate the full height
        if (expand) {
          this.el.open = true;
        }

        // Calculate the end height based on expanding or shrinking
        const endHeight = expand
          ? `${this.summary.offsetHeight + this.content.offsetHeight}px`
          : `${this.summary.offsetHeight}px`;

        // If there's an existing animation, cancel it
        if (this.animation) {
          this.animation.cancel();
        }

        // Animate between the start and end height
        this.animation = this.el.animate(
          {
            height: [startHeight, endHeight],
          },
          {
            duration: 350,
            easing: 'ease-in-out',
          }
        );

        // Handle when the animation finishes
        this.animation.onfinish = () => this.onAnimationFinish(expand);
        this.animation.oncancel = () => {
          this.isClosing = this.isExpanding = false; // Reset states if animation is canceled
        };
      }

      onAnimationFinish(expand) {
        // If shrinking, set `open` to false
        if (!expand) {
          this.el.open = false;
        }

        // Reset the animation and state
        this.animation = null;
        this.isClosing = false;
        this.isExpanding = false;

        // Clear inline styles to reset the layout
        this.el.style.height = '';
        this.el.style.overflow = '';
      }
    }
  );
}


if (!customElements.get('custom-slider')) {
  customElements.define(
    'custom-slider',
    class CustomSlider extends HTMLElement {
      constructor() {
        super()
        this.swiperSlidesToActive = +this.dataset.swiperSlidesToActive;
        //class name of the swiper
        this.swiperId = this.dataset.swiperClassName;
        //class name of the swiper wrapper
        this.swiperWrapper = this.dataset.swiperWrapperClassName;
        this.swiperSlide = this.dataset.swiperSlideClassName;
        this.swiperSlidesPerView = this.dataset.swiperSlidesPerView;
        this.swiperSlideList = this.querySelectorAll(`.${this.swiperSlide}`)
        this.withoutSwiperPosition = this.dataset.withoutSwiperPosition;
        this.swiperSlideLength = this.swiperSlideList.length;
      }

      connectedCallback() {
        this.initializeSwiper()
      }

      initializeSwiper() {
        const slidesPerView = this.swiperSlidesPerView ? parseFloat(this.swiperSlidesPerView) : 2;
        if (this.swiperSlideLength > this.swiperSlidesToActive) {
          this.swiperSlideList.forEach(slide => slide.classList.add('swiper-slide'))
          this.querySelector(`.${this.swiperWrapper}`).classList.add('swiper-wrapper');
          this.querySelector(`.${this.swiperId}`).classList.add('swiper');
          new Swiper(`.${this.swiperId}`, {
            loop: true,
            speed: 600,
            slidesPerView: slidesPerView,
            spaceBetween: 20,
            navigation: {
              nextEl: '.slider-button--next',
              prevEl: '.slider-button--prev',
            }
          });
        } else if (this.swiperSlideLength === 1) {
          this.querySelector(`.${this.swiperWrapper}`).classList.add(`complete-the-look-without-slider__center`);
        } else {
          this.querySelector(`.${this.swiperWrapper}`).classList.add(`complete-the-look-without-slider__${this.withoutSwiperPosition || 'left'}`);
        }
      }
    }
  );
}


if (!customElements.get('flashing-announcement')) {
  customElements.define(
    'flashing-announcement',
    class FlashingAnnouncement extends HTMLElement {
      constructor() {
        super();
        this.messages = Array.from(this.querySelectorAll('.announcement-bar__message'));
        this.currentMessageIndex = 0;
        this.duration = +this.dataset.changeSlideSpeed;
      }

      connectedCallback() {
        // Rotate messages every 5 seconds
        this.interval = setInterval(() => this.showNextMessage(), this.duration * 1000);
      }

      disconnectedCallback() {
        clearInterval(this.interval); // Clean up interval when the element is removed
      }

      showNextMessage() {
        this.messages[this.currentMessageIndex].classList.add('fade-out');

        // Wait for the fade-out animation to complete before switching
        setTimeout(() => {
          // Hide the current message after fade-out
          this.messages[this.currentMessageIndex].classList.add('hidden');
          this.messages[this.currentMessageIndex].classList.remove('fade-out');

          // Move to the next message
          this.currentMessageIndex = (this.currentMessageIndex + 1) % this.messages.length;

          // Show the next message with fade-in effect
          this.messages[this.currentMessageIndex].classList.remove('hidden');
          this.messages[this.currentMessageIndex].classList.add('fade-in');

          // Remove the fade-in class after the animation completes
          setTimeout(() => {
            this.messages[this.currentMessageIndex].classList.remove('fade-in');
          }, 300);
        }, 300);
      }
    }
  )
}

class shippingBar extends HTMLElement {
  constructor() {
    super();
    
    this.convert = document.querySelector('[data-shipping-bar="convert"]')
    this.progress = this.querySelector('progress');
    this.readyElement = this.querySelector('[data-shipping-bar="ready"]');
    this.continueElement = this.querySelector('[data-shipping-bar="continue"]');
    this.differenceElement = this.querySelector('[data-shipping-bar="difference"]');
    
    this.maxPrice = this.progress.getAttribute('max');
    this.cartTotal = this.progress.getAttribute('value');
  }

  connectedCallback() {
    if (!this.convert) return;
    
    this.init();
  }
  
  init() {
    this.roundedMoney = Math.ceil(this.convertedMoney);

    if (this.roundedMoney > this.cartTotal) {
      this.showContinueElement();
    } else {
      this.showReadyElement();
    }

    this.differenceElement.textContent = this.formattedMoney;
    this.updateProgress(this.roundedMoney);
  }
  
  get convertedMoney() {
    return this.convert
        ? window.Currency.convert(this.maxPrice, 'USD', Shopify.currency.active)
        : this.maxPrice;
  }

  get formattedMoney() {
    return formatMoney(this.roundedMoney - this.cartTotal, '${{amount_no_decimals}}')
        .replace('$', `${window.CURRENT_CURRENCY_SYMBOL} `);
  }
  
  updateProgress(maxPrice) {
    this.progress.setAttribute('value', this.cartTotal);
    this.progress.setAttribute('max', maxPrice);
  }
  
  showContinueElement() {
    this.continueElement.classList.remove('hidden');
    this.readyElement.classList.add('hidden');
  }
  
  showReadyElement() {
    this.continueElement.classList.add('hidden');
    this.readyElement.classList.remove('hidden');
  }
}

customElements.define('shipping-bar', shippingBar);
